import { FC, PropsWithChildren, useEffect } from "react"
import { useTranslation } from "react-i18next"
import { useUserQuery } from "../api/users-api"
import { AppBlockScreen } from "./AppBlockScreen"
import { isPixiesetAuthEnabled } from "@/utils/measurement/feature-flag"
import { pixiesetSubdomains } from "@/utils/pixieset-urls"
import { activateAmplitudeForUser, analytics } from "@/telemetry/amplitude"
import { setGlobalSentryContext } from "@/telemetry/sentry-peers"
import { useAtomValue } from "jotai"
import { isAccountMigratedAtom } from "@/store/auth-state"
import { preloadExperiments } from "@/utils/measurement/experiment"

export const AppWhitelistGuard: FC<PropsWithChildren> = ({ children }) => {
	const { t } = useTranslation("components")
	const userQuery = useUserQuery(false)
	const pnUser = userQuery.data
	const isPixiesetAuth = isPixiesetAuthEnabled()
	const isAccountMigrated = useAtomValue(isAccountMigratedAtom)

	useEffect(() => {
		if (pnUser) {
			setGlobalSentryContext("user", {
				username: pnUser.displayName ?? "John Doe", // i18n-ignore: Sentry fallback, not user-visible
				id: pnUser.userId ?? "00000000-0000-0000-0000-000000000000",
			})
			analytics.setUserInfo(pnUser)
			void preloadExperiments().catch((error) => {
				console.warn("Failed to preload experiments.", error)
			})

			/**
			 * Defer Amplitude initialization, events will still be tracked,
			 * but sent after initialization
			 */
			const timeoutId = window.setTimeout(() => {
				activateAmplitudeForUser(pnUser)
			}, 3000)

			return () => {
				window.clearTimeout(timeoutId)
			}
		}
	}, [pnUser])

	if (isPixiesetAuth && userQuery.isLoading) {
		return null
	}

	if (isAccountMigrated) {
		return (
			<AppBlockScreen
				reason={
					<>
						{t("appWhitelistGuard.accountMigrated")}{" "}
						<a
							href={pixiesetSubdomains.editor}
							className="underline"
							target="_blank"
							rel="noopener noreferrer"
						>
							{new URL(pixiesetSubdomains.editor).hostname}
						</a>
						.
					</>
				}
			/>
		)
	}

	let reason: string | null = null
	if (pnUser?.isWhitelisted === false) {
		reason = isPixiesetAuth
			? t("appWhitelistGuard.noPermission")
			: t("appWhitelistGuard.pendingApproval", { email: pnUser.email })
	}

	if (reason) {
		return <AppBlockScreen reason={reason} />
	}

	return <>{children}</>
}
